import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { configAgentError, forexApi, type ConfigAgentV1, type ConfigAgentDeletionPreview } from "@/lib/forex-api";
import ConfigAgentEditor from "./ConfigAgentEditor";

export default function ConfigAgentListTab() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ConfigAgentV1[]>([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<ConfigAgentDeletionPreview | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [requestIds, setRequestIds] = useState<Record<string, string>>({});
  const [editingId, setEditingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [configFilter, setConfigFilter] = useState("");
  const [runtimeFilter, setRuntimeFilter] = useState("");
  const [sourceTotal, setSourceTotal] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const result = await forexApi.listConfigAgents();
      setItems(result.items);
      setSourceTotal(result.total);
      setError(null);
    } catch {
      setError("智能体列表暂不可用，请稍后刷新");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);
  const visibleItems = items.filter(agent =>
    (!search || `${agent.name} ${agent.agent_id}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())) &&
    (!configFilter || (configFilter === "active" ? !!agent.active_config_id : !agent.active_config_id)) &&
    (!runtimeFilter || agent.runtime === runtimeFilter));

  const startDelete = async (agent: ConfigAgentV1) => {
    setPending(agent.agent_id);
    setError(null);
    setMessage(null);
    try {
      setPreview(await forexApi.getConfigAgentDeletionPreview(agent.agent_id));
    } catch {
      setError("删除预览读取失败，请刷新后重试");
    } finally {
      setPending(null);
    }
  };

  const confirmDelete = async () => {
    if (!preview || pending) return;
    const agentId = preview.agent_id;
    const key = requestIds[agentId] || crypto.randomUUID();
    setRequestIds((current) => ({ ...current, [agentId]: key }));
    setPending(agentId);
    setError(null);
    setMessage(null);
    try {
      const result = await forexApi.deleteConfigAgent(agentId, preview.row_revision, "管理员删除智能体并解绑主账号", key);
      if (result.status === "deleted") {
        setMessage(`智能体已删除，主账号 ${result.unbound_account_id ?? ""} 已解绑`);
        setPreview(null);
        setRequestIds((current) => { const next = { ...current }; delete next[agentId]; return next; });
      } else {
        setMessage("智能体正在停止新研究并处理所有账号的执行、对账及退出义务；完成后解绑平台账号");
        setPreview(null);
      }
      await refresh();
    } catch (cause) {
      setError(configAgentError(cause, "删除结果尚未确认。请保留当前页面并刷新状态；再次提交会使用同一操作标识。"));
    } finally {
      setPending(null);
    }
  };

  return <div className="space-y-4">
    <div className="flex items-center justify-between">
      <div><h2 className="text-lg font-semibold text-text-primary">智能体列表</h2><p className="text-xs text-text-muted">策略研究状态与逐账号执行状态分开；审核通过不等于所有账号可交易。</p></div>
      <div className="flex gap-2"><button onClick={() => navigate("/forex/agents/create")} className="px-3 py-1.5 rounded-lg bg-brand text-white text-sm">新建智能体</button>
        <button onClick={() => void refresh()} className="px-3 py-1.5 rounded-lg border border-surface-border text-sm">刷新</button></div>
    </div>
    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
    {message && <p role="status" className="text-sm text-emerald-600">{message}</p>}
    <div className="flex flex-wrap items-center gap-2 text-sm"><input aria-label="查找已加载智能体" value={search} onChange={event=>setSearch(event.target.value)} placeholder="名称或 ID" className="rounded-lg border border-surface-border px-3 py-2"/><select aria-label="配置状态" value={configFilter} onChange={event=>setConfigFilter(event.target.value)} className="rounded-lg border border-surface-border px-3 py-2"><option value="">全部配置</option><option value="active">已审核生效</option><option value="draft">草稿</option></select><select aria-label="研究运行状态" value={runtimeFilter} onChange={event=>setRuntimeFilter(event.target.value)} className="rounded-lg border border-surface-border px-3 py-2"><option value="">全部研究状态</option><option value="running">研究运行中</option><option value="stopped">研究已停止</option></select><span className="text-xs text-text-muted">当前加载 {items.length}/{sourceTotal} 个 · 筛选显示 {visibleItems.length} 个{sourceTotal>items.length?'；查找仅覆盖当前加载项':''}</span></div>
    <div className="overflow-x-auto rounded-xl border border-surface-border bg-white">
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-surface/50 text-left"><th className="p-3">智能体</th><th className="p-3">主账号</th><th className="p-3">配置</th><th className="p-3">运行</th><th className="p-3">操作</th></tr></thead>
        <tbody>
          {visibleItems.map((agent) => <tr key={agent.agent_id} className="border-b border-surface-border">
            <td className="p-3"><button type="button" onClick={() => navigate(`/forex/agents/detail?id=${encodeURIComponent(agent.agent_id)}`)} className="text-left text-brand hover:underline">{agent.name}</button><div className="text-xs text-text-muted font-mono">{agent.agent_id}</div></td>
            <td className="p-3 font-mono">{agent.master_account_id || '未绑定'}</td>
            <td className="p-3">{agent.active_config_id ? "已审核生效" : "草稿"} · 修订 {agent.draft_revision}{agent.active_template_selection?.strategy&&<small className="block text-text-muted">策略 {agent.active_template_selection.strategy.id} · v{agent.active_template_selection.strategy.version}</small>}</td>
            <td className="p-3">{agent.lifecycle === "deleting" ? "停止新研究，账号处理中" : agent.runtime === "running" ? "策略研究运行中" : agent.runtime === "stopped" ? "策略研究已停止" : "研究状态待确认"}</td>
            <td className="p-3"><div className="flex gap-2"><button onClick={() => setEditingId(agent.agent_id)}
              disabled={!!pending || agent.lifecycle === "deleting"}
              className="px-3 py-1.5 rounded-lg border border-surface-border disabled:opacity-50">编辑</button>
              <button onClick={() => navigate(`/forex/agents/detail?id=${encodeURIComponent(agent.agent_id)}`)}
              className="px-3 py-1.5 rounded-lg border border-surface-border">详情与运行</button>
              <details className="relative"><summary className="cursor-pointer px-3 py-1.5 rounded-lg border border-surface-border">更多</summary><button onClick={() => void startDelete(agent)} disabled={!!pending || agent.lifecycle === "deleting"}
              className="mt-1 px-3 py-1.5 rounded-lg text-red-600 bg-red-50 disabled:opacity-50">删除</button></details></div></td>
          </tr>)}
          {!loading && visibleItems.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-text-muted">{items.length?'当前加载项中没有符合条件的智能体':'暂无智能体'}</td></tr>}
        </tbody>
      </table>
    </div>
    {editingId && <div role="dialog" aria-modal="true" aria-label="编辑智能体配置" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <div className="flex justify-end"><button onClick={() => { setEditingId(null); void refresh(); }} className="px-3 py-1 rounded border">关闭</button></div>
        <ConfigAgentEditor agentId={editingId === "new" ? undefined : editingId} onDone={() => void refresh()} />
      </div>
    </div>}
    {preview && <div role="dialog" aria-modal="true" aria-label="删除智能体" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl space-y-4">
        <h3 className="text-lg font-semibold">删除智能体并解绑平台账号</h3>
        <p className="text-sm">{preview.name}（{preview.agent_id}）绑定平台账号 {preview.master_account_id}。</p>
        <p className="text-sm text-text-secondary">涉及 {preview.delete_counts.configs} 份配置、{preview.delete_counts.reviews} 条审核、{preview.delete_counts.targets ?? 0} 个账号目标和 {preview.delete_counts.reservations ?? 0} 项额度占用。</p>
        {preview.inflight_count > 0 && <p className="text-sm text-amber-600">存在 {preview.inflight_count} 项未完成义务（所有账号的命令、仓位、退出或对账）。先停止新研究，确认后才能完成解绑。</p>}
        {preview.blockers && Object.entries({ commands: "执行中或待对账命令", permits: "有效提交许可", positions: "未平仓子仓", exit_obligations: "未完成退出义务" }).map(([key, label]) => (
          (preview.blockers?.[key] ?? 0) > 0 && <p key={key} className="text-sm text-amber-700">{label}：{preview.blockers[key]} 项</p>
        ))}
        <div className="flex justify-end gap-2"><button onClick={() => setPreview(null)} className="px-4 py-2 rounded-lg border">取消</button>
          <button onClick={() => void confirmDelete()} disabled={!!pending || !preview.can_start}
            className="px-4 py-2 rounded-lg bg-red-600 text-white disabled:opacity-50">删除并解绑</button></div>
      </div>
    </div>}
  </div>;
}
